/**
 * NoteLegsTable — the investor legs of one note, laid out like the source workbook:
 * Code · Client · Staff? · Company? · Pay dates · Nominal · Gross/Net rate · Maturity · Issue ·
 * Days · Gross/Net interest · Commission · WHT · Net due · Total due, with a footer total.
 *
 * Figures come from the API (stored schedule for submitted legs; live preview for drafts, shown
 * in italics with a "preview" marker). A leg the API could not price shows the reason instead
 * of figures — never a silent zero.
 */
import { Fragment } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip, Typography, Box,
} from '@mui/material';
import { formatNumber, formatShortDate } from '../../utils/formatters';

export const SPREAD_SOURCES = {
  manual: 'Entered for this investor', override: 'Investor commission override', staff: 'Staff spread', standard: 'Standard spread',
};

const yesNo = (v) => (v === null || v === undefined ? '-' : v ? 'Yes' : 'No');
const pct = (v) => (v === null || v === undefined ? '-' : `${Number(v).toFixed(2)}%`);
const money = (v) => (v === null || v === undefined ? '-' : formatNumber(v, 2));

/** Staff/company flags: the snapshot once submitted, otherwise the live pricing preview. */
const flag = (leg, key) => (leg[key] !== null && leg[key] !== undefined ? leg[key] : leg.pricing?.[key]);

const NoteLegsTable = ({ note, onRowClick, renderActions, dense = false }) => {
  const legs = note.legs || [];
  const s = note.summary || {};
  const cell = { whiteSpace: 'nowrap', py: dense ? 0.5 : 1 };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Code', 'Client', 'Spread', 'Staff?', 'Company?', 'Int Pay Dates', 'Nominal', 'Gross Rate',
              'Net Rate', 'Maturity', 'Issue Date', 'Days', 'Gross Interest', 'Net Interest', 'Commission',
              'WHT', 'Net Interest Due', 'Total Due at Maturity'].map((h) => (
              <TableCell key={h} sx={{ ...cell, fontWeight: 600 }}
                align={['Code', 'Client', 'Int Pay Dates', 'Maturity', 'Issue Date'].includes(h) ? 'left' : 'right'}>
                {h}
              </TableCell>
            ))}
            {renderActions && <TableCell sx={{ ...cell, fontWeight: 600 }} align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {legs.map((leg) => {
            const f = leg.figures;
            const preview = leg.is_preview;
            const fig = (v) => (
              <Typography component="span" variant="body2" sx={preview ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}>
                {money(v)}
              </Typography>
            );
            return (
              <TableRow key={leg.id} hover={Boolean(onRowClick)} onClick={onRowClick ? () => onRowClick(leg) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}>
                <TableCell sx={cell}>{leg.code}</TableCell>
                <TableCell sx={cell}>{leg.investor_name}</TableCell>
                <TableCell sx={cell} align="right">
                  <Tooltip title={leg.pricing ? `Source: ${SPREAD_SOURCES[leg.pricing.spread_source] || leg.pricing.spread_source}` : ''}>
                    <span>{pct(leg.commission_spread ?? leg.pricing?.commission_spread)}</span>
                  </Tooltip>
                  {leg.pricing?.spread_source === 'manual' && <Chip size="small" label="edited" variant="outlined" sx={{ ml: 0.5, height: 18 }} />}
                  {preview && <Chip size="small" label="preview" variant="outlined" sx={{ ml: 0.5, height: 18 }} />}
                </TableCell>
                <TableCell sx={cell} align="right">{yesNo(flag(leg, 'is_staff'))}</TableCell>
                <TableCell sx={cell} align="right">{yesNo(flag(leg, 'is_company'))}</TableCell>
                <TableCell sx={cell}>{note.interest_frequency}</TableCell>
                <TableCell sx={cell} align="right">{money(leg.nominal_amount)}</TableCell>
                <TableCell sx={cell} align="right">{pct(note.gross_rate)}</TableCell>
                <TableCell sx={cell} align="right">
                  <Tooltip title={leg.pricing ? `Spread ${pct(leg.pricing.commission_spread)} (${leg.pricing.spread_source})` : ''}>
                    <span>{pct(leg.client_rate ?? leg.pricing?.client_rate)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={cell}>{formatShortDate(note.maturity_date)}</TableCell>
                <TableCell sx={cell}>
                  {formatShortDate(leg.issue_date)}
                  {leg.issue_date_override && (
                    <Tooltip title={`Overrides the note's start date (${formatShortDate(note.issue_date)})`}>
                      <Chip size="small" label="own date" variant="outlined" sx={{ ml: 0.5, height: 18 }} />
                    </Tooltip>
                  )}
                </TableCell>
                {leg.pricing_error ? (
                  <TableCell sx={cell} colSpan={7}>
                    <Typography variant="body2" color="error">Cannot price: {leg.pricing_error}</Typography>
                  </TableCell>
                ) : (
                  <Fragment>
                    <TableCell sx={cell} align="right">{f ? f.days : '-'}</TableCell>
                    <TableCell sx={cell} align="right">{fig(f?.gross_interest)}</TableCell>
                    <TableCell sx={cell} align="right">{fig(f?.client_interest)}</TableCell>
                    <TableCell sx={cell} align="right">{fig(f?.commission_amount)}</TableCell>
                    <TableCell sx={cell} align="right">
                      {f && f.wht_amount === 0 ? '-' : fig(f?.wht_amount)}
                    </TableCell>
                    <TableCell sx={cell} align="right">{fig(f?.client_net_due)}</TableCell>
                    <TableCell sx={cell} align="right">{fig(f?.total_due)}</TableCell>
                  </Fragment>
                )}
                {renderActions && (
                  <TableCell sx={cell} align="right" onClick={(e) => e.stopPropagation()}>
                    {renderActions(leg)}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {legs.length > 0 && (
            <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
              <TableCell sx={cell} colSpan={6}>Total ({legs.length} leg{legs.length === 1 ? '' : 's'})</TableCell>
              <TableCell sx={cell} align="right">{money(s.nominal)}</TableCell>
              <TableCell sx={cell} colSpan={5} />
              <TableCell sx={cell} align="right">{money(s.gross_interest)}</TableCell>
              <TableCell sx={cell} align="right">{money(s.client_interest)}</TableCell>
              <TableCell sx={cell} align="right">{money(s.commission_amount)}</TableCell>
              <TableCell sx={cell} align="right">{money(s.wht_amount)}</TableCell>
              <TableCell sx={cell} align="right">{money(s.client_net_due)}</TableCell>
              <TableCell sx={cell} align="right">{money(s.total_due)}</TableCell>
              {renderActions && <TableCell sx={cell} />}
            </TableRow>
          )}
          {legs.length === 0 && (
            <TableRow>
              <TableCell colSpan={renderActions ? 19 : 18}>
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No investor legs yet.</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NoteLegsTable;
